/**
 * Per-event upload-scheduler policy editor — embedded in EventTab's
 * right column. Two policies (high_only / deferred_high), with sub-flags
 * surfaced only when relevant. Backed by:
 *   - GET /api/fleet/events/{id}/upload-policy
 *   - PUT /api/fleet/events/{id}/upload-policy   (require_authz-guarded:
 *     organizer(own event)/superadmin — session cookie + CSRF)
 *
 * The PUT also fans out a fresh `geofence_set` to every armed enrollment
 * so online phones pick up the new policy within ~1 RTT — same pattern
 * as gate edits.
 *
 * Mode handling:
 *   - "High quality only" — upload ONLY the High-Quality tier, live, even
 *     on cellular. The low-quality preview tier is never produced or
 *     uploaded; single tier, no backfill.
 *   - "Deferred high" — low-quality preview only during recording;
 *     High-Quality archive after. Sub-flag `wifi_only_archive` gates the
 *     High-Quality drain on a wifi uplink.
 *   - "Inherit global default" — all override fields are sent as null
 *     so the backend resolves from `CaptureGlobalConfig.default_*`.
 */

import React, { useEffect, useState } from 'react';
import { Cloud, Loader2, Save, UploadCloud, Wifi } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import api from '@/services/api';
import type { EventUploadPolicy } from '@/types';

type ModeChoice = 'inherit' | 'high_only' | 'deferred_high';

interface Props {
  eventId: number | null;
}

export const UploadPolicyCard: React.FC<Props> = ({ eventId }) => {
  const [resolved, setResolved] = useState<EventUploadPolicy | null>(null);
  // Local edit state. `inherit` means "send nulls so backend uses defaults".
  // We can't tell from the resolved payload whether the event actually
  // overrode or inherited (the resolved view collapses the two), so the
  // initial mode mirrors the resolved value; the operator can pick
  // "Inherit" explicitly to wipe the override.
  const [mode, setMode] = useState<ModeChoice>('inherit');
  const [wifiOnly, setWifiOnly] = useState<boolean>(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    if (eventId == null) {
      setResolved(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErrorText(null);
    (async () => {
      try {
        const data = await api.getEventUploadPolicy(eventId);
        if (cancelled) return;
        setResolved(data);
        // Seed the form with the currently-effective values.
        setMode(data.mode as ModeChoice);
        setWifiOnly(data.wifi_only_archive);
      } catch (e) {
        if (cancelled) return;
        const msg =
          (e as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ??
          (e as { message?: string })?.message ??
          'failed to load';
        setErrorText(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const handleSave = async () => {
    if (eventId == null) return;
    setSaving(true);
    setErrorText(null);
    try {
      const body =
        mode === 'inherit'
          ? {
              upload_policy: null,
              adaptive_enter_low_s: null,
              adaptive_recover_high_s: null,
              wifi_only_archive: null,
            }
          : mode === 'high_only'
            ? {
                upload_policy: 'high_only' as const,
                adaptive_enter_low_s: null,
                adaptive_recover_high_s: null,
                wifi_only_archive: null,
              }
            : {
                upload_policy: 'deferred_high' as const,
                adaptive_enter_low_s: null,
                adaptive_recover_high_s: null,
                wifi_only_archive: wifiOnly,
              };
      const updated = await api.updateEventUploadPolicy(eventId, body);
      setResolved(updated);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ??
        (e as { message?: string })?.message ??
        'save failed';
      setErrorText(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <UploadCloud className="h-4 w-4 text-accent" />
          <h2 className="text-base font-semibold">Upload policy</h2>
          {resolved && (
            <span className="ml-auto text-[10px] text-fg-secondary">rev {resolved.revision}</span>
          )}
        </div>
        {eventId == null && (
          <p className="text-xs text-fg-secondary">Select an event to edit its upload policy.</p>
        )}
        {eventId != null && loading && (
          <div className="flex items-center gap-2 text-xs text-fg-secondary">
            <Loader2 className="h-3 w-3 animate-spin" /> loading…
          </div>
        )}
        {eventId != null && !loading && (
          <div className="space-y-3">
            <div className="space-y-2">
              <ModeRow
                value="inherit"
                current={mode}
                onSelect={() => setMode('inherit')}
                title="Inherit global default"
                hint="Use whatever Recording-settings card has under Default upload policy."
              />
              <ModeRow
                value="high_only"
                current={mode}
                onSelect={() => setMode('high_only')}
                title="High quality only (recommended)"
                hint="Uploads only the High-Quality tier, live — even on cellular. No low-quality preview, no backfill."
              />
              <ModeRow
                value="deferred_high"
                current={mode}
                onSelect={() => setMode('deferred_high')}
                title="Low quality live, High quality after"
                hint="Low-quality preview uploads live during recording; the High-Quality tier backfills after the session ends. One tier at a time."
              />
            </div>

            {mode === 'high_only' && (
              <div className="space-y-2 rounded-md border border-border bg-bg-elevated/40 p-3">
                <p className="text-[11px] text-fg-secondary">
                  Single tier: only the High-Quality recording is uploaded, live — even
                  over cellular. The low-quality preview tier is never produced or
                  uploaded, so there's no second stream and no backfill phase.
                </p>
              </div>
            )}

            {mode === 'deferred_high' && (
              <div className="space-y-2 rounded-md border border-border bg-bg-elevated/40 p-3">
                <div className="flex items-start gap-2">
                  <Wifi className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">High quality only on wifi</span>
                      <Switch checked={wifiOnly} onCheckedChange={setWifiOnly} />
                    </div>
                    <p className="mt-1 text-[10px] text-fg-secondary">
                      When on, the High-Quality upload pauses until a wifi uplink is
                      available — saves cellular data when the low-quality preview is
                      good enough live.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {resolved && (
              <div className="rounded-md border border-border bg-bg-elevated/20 p-2 text-[10px] text-fg-secondary">
                <div className="mb-1 flex items-center gap-1 font-medium">
                  <Cloud className="h-3 w-3" />
                  Currently effective on phones
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <span>Mode</span>
                  <span className="font-mono">{resolved.mode}</span>
                  <span>Wifi-only HQ</span>
                  <span className="font-mono">{resolved.wifi_only_archive ? 'yes' : 'no'}</span>
                </div>
              </div>
            )}

            {errorText && <p className="text-xs text-destructive">{errorText}</p>}

            <div className="flex items-center justify-end">
              <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 px-2 text-xs">
                <Save className="mr-1 h-3 w-3" /> Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ModeRow: React.FC<{
  value: ModeChoice;
  current: ModeChoice;
  onSelect: () => void;
  title: string;
  hint: string;
}> = ({ value, current, onSelect, title, hint }) => {
  const selected = value === current;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-md border p-2 text-left transition-colors ${
        selected
          ? 'border-accent bg-accent/10'
          : 'border-border bg-bg-elevated/40 hover:bg-bg-elevated/60'
      }`}
    >
      <div className="flex items-center gap-2">
        <div
          className={`h-3 w-3 rounded-full border-2 ${
            selected ? 'border-accent bg-accent' : 'border-border'
          }`}
        />
        <span className="text-sm font-medium">{title}</span>
      </div>
      <p className="mt-1 pl-5 text-[10px] text-fg-secondary">{hint}</p>
    </button>
  );
};

export default UploadPolicyCard;
